import { APP_INITIALIZER, ApplicationConfig, PLATFORM_ID, REQUEST, importProvidersFrom, inject } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi
} from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ServiceWorkerModule } from '@angular/service-worker';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { InViewportModule } from 'ng-in-viewport';
import { HotToastModule } from '@ngneat/hot-toast';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { catchError, firstValueFrom, of } from 'rxjs';
import enTranslations from '../assets/i18n/en.json';
import itTranslations from '../assets/i18n/it.json';
import { APP_ROUTES } from './app.routes';
import { AppSetting } from './app.setting';
import { AuthInterceptor } from './services/interceptor/auth.interceptor';
import { initializeKeycloak } from './services/auth/keycloak-init.factory';
import { errorInterceptorProviders } from './services/interceptor/errors.interceptor';
import { TerritoryService } from './services/geolocation/territory.service';

const STATIC_TRANSLATIONS: Record<'it' | 'en', Record<string, unknown>> = {
  it: itTranslations as Record<string, unknown>,
  en: enTranslations as Record<string, unknown>
};

class StaticTranslateLoader implements TranslateLoader {
  getTranslation(lang: string) {
    const normalizedLanguage = normalizeSupportedLanguage(lang) ?? 'it';
    return of(STATIC_TRANSLATIONS[normalizedLanguage]);
  }
}

export function createTranslateLoader() {
  return new StaticTranslateLoader();
}

function normalizeSupportedLanguage(language: string | null): 'it' | 'en' | null {
  if (!language) {
    return null;
  }

  const normalizedLanguage = language.toLowerCase().trim();
  if (normalizedLanguage.startsWith('it')) {
    return 'it';
  }
  if (normalizedLanguage.startsWith('en')) {
    return 'en';
  }

  return null;
}

function getRequestHeader(request: any, headerName: string): string | null {
  if (!request?.headers) {
    return null;
  }

  const normalizedHeaderName = headerName.toLowerCase();
  const headerValue = request.headers[normalizedHeaderName] ?? request.headers[headerName];

  if (Array.isArray(headerValue)) {
    return headerValue[0] ?? null;
  }

  return typeof headerValue === 'string' ? headerValue : null;
}

function resolveServerLanguage(request: any): 'it' | 'en' {
  const rawUrl = request?.originalUrl ?? request?.url ?? '/';
  const queryLanguage = normalizeSupportedLanguage(new URL(rawUrl, 'http://localhost').searchParams.get('lang'));
  if (queryLanguage) {
    return queryLanguage;
  }

  const acceptedLanguage = getRequestHeader(request, 'accept-language');
  const primaryAcceptedLanguage = acceptedLanguage?.split(',')[0] ?? null;
  return normalizeSupportedLanguage(primaryAcceptedLanguage) ?? 'it';
}

function resolveBrowserLanguage(): 'it' | 'en' {
  const queryLanguage = normalizeSupportedLanguage(new URLSearchParams(window.location.search).get('lang'));
  if (queryLanguage) {
    return queryLanguage;
  }

  const storedLanguage = normalizeSupportedLanguage(localStorage.getItem('language'));
  if (storedLanguage) {
    return storedLanguage;
  }

  return normalizeSupportedLanguage(window.navigator.language) ?? 'it';
}

export function initializeTranslations() {
  const translateService = inject(TranslateService);
  const platformId = inject(PLATFORM_ID);
  const request = inject(REQUEST, { optional: true }) as any;

  return () => {
    const initialLanguage = isPlatformBrowser(platformId)
      ? resolveBrowserLanguage()
      : resolveServerLanguage(request);

    translateService.setDefaultLang('it');
    translateService.setTranslation('it', STATIC_TRANSLATIONS.it, true);
    translateService.setTranslation('en', STATIC_TRANSLATIONS.en, true);
    return firstValueFrom(translateService.use(initialLanguage).pipe(catchError(() => of({}))));
  };
}

export function initializeTerritoryContext() {
  const territoryService = inject(TerritoryService);
  const document = inject(DOCUMENT);

  return () => {
    const territoryCode = territoryService.getTerritoryCodeFromUrl();
    document.documentElement.setAttribute('data-territory', territoryCode);
    return Promise.resolve();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      InViewportModule,
      InfiniteScrollDirective,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader
        }
      }),
      ServiceWorkerModule.register('ngsw-worker.js', {
        registrationStrategy: 'registerWhenStable:30000'
      }),
      ReactiveFormsModule,
      FormsModule,
      KeycloakAngularModule,
      HotToastModule.forRoot()
    ),
    AppSetting,
    errorInterceptorProviders,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      multi: true,
      deps: [KeycloakService, PLATFORM_ID]
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTranslations,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTerritoryContext,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    provideRouter(APP_ROUTES),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideAnimations(),
    provideClientHydration(withEventReplay())
  ]
};
